/**
 * Split from SideBar.js so it can be tested: anything importing that file
 * pulls in react-router-dom, which CRA's Jest cannot transform.
 */

/**
 * The nav a client actually gets: the standard items, with its own modules
 * folded in.
 *
 * Hegic is three products behind one login — claims, break-in and PAYD — and
 * the only way between them used to be signing out to the picker.
 *
 * Two rules, both learned the hard way:
 *
 * A module is listed only where a redesigned page exists, which the /redesign
 * prefix tells us. One still pointing at an old-design path would drop the
 * reader out of this chrome, which is the seam this nav exists to avoid.
 *
 * A module whose path is ALREADY a standard item does not get a second entry —
 * Hegic's Claims is /redesign/cases, which is the Cases item. It renames that
 * item instead, so the nav reads "Claims" for the client who calls it that and
 * "Cases" for everyone else, and never both.
 *
 * NOTE ON WHO SEES THESE: everyone whose client declares them. That matches
 * the module picker, whose own comment records that the mapping from
 * /get_user_permissions codes to modules is not established — so there is no
 * honest way to filter yet, and guessing one would hide a product from
 * somebody entitled to it.
 */
/** Nav items a client's own products share, so they read as one set. */
export const MODULE_GROUP = "modules";

/**
 * The post-login picker already calls them modules to the user ("Select
 * Module", src/components/ModuleSelect.js), so the rail says the same word
 * rather than inventing a second name for the same thing.
 */
export const MODULE_GROUP_LABEL = "Modules";

export const moduleNavItems = (client) =>
  (client?.modules || [])
    .filter((module) => String(module?.path || "").startsWith("/redesign/"))
    .map((module) => ({
      id: `module-${module.key}`,
      label: module.label || module.key,
      path: module.path,
      group: MODULE_GROUP,
    }));

export const withClientModules = (navItems = [], client) => {
  const modules = moduleNavItems(client);
  // A client with no redesigned modules gets its own array back, untouched —
  // identity, not a copy. Every client but Hegic lands here, so "nothing
  // changes for them" is structural rather than something to remember.
  if (modules.length === 0) return navItems;

  const byPath = new Map(modules.map((module) => [module.path, module]));
  const claimed = [];
  const rest = [];

  navItems.forEach((item) => {
    const module = item.path && byPath.get(item.path);
    if (!module) {
      rest.push(item);
      return;
    }
    byPath.delete(item.path);
    // The client's own name for it, on the item that already goes there — and
    // moved into the group, because Claims belongs beside Break-in and PAYD
    // rather than two rows above them with People in between. Keeps its id, so
    // the case-count pill and the analytics override still find it.
    claimed.push({ ...item, label: module.label, group: MODULE_GROUP });
  });

  return [...claimed, ...byPath.values(), ...rest];
};

/**
 * Consecutive items sharing a group become one section.
 *
 * Order is preserved and an ungrouped nav yields exactly one section, so a
 * client without modules renders precisely the list it always did.
 */
export const navSections = (items = []) =>
  items.reduce((sections, item) => {
    const last = sections[sections.length - 1];
    if (last && last.group === item.group) {
      last.items.push(item);
      return sections;
    }
    return [...sections, { group: item.group, items: [item] }];
  }, []);
